import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type AdminServiceQueueRowView, type AdminServiceQueueView } from '@vello/api-client';
import { SERVICE_LINES } from '@vello/provider-services';
import { AUTO_REFRESH_MS, Badge, EmptyState, Field, FormRow, Notice, PageHeader, Panel, Select, Stack, Table, Text, useAutoRefresh } from '@vello/web-theme';
import { enrollmentTone, formatMarketInstant, marketTimeZone, QUEUE_STATE_FILTERS, queueStates, type QueueStateFilter } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { MarketField } from '../components/market-field';
import { usePhoneText } from '../components/phone-text';
import { ServiceRequestDrawer } from '../components/provider-services';

const SURFACE = 'refusal.admin.provider_services';

/**
 * **Prestadores › Serviços em análise** (D883, program D871 phase 5, ADR-0160) over `GET /v1/admin/provider-services/queue` (`driver:view`, GEO at the market).
 *
 * - Each provider's request for one service line, the one waiting longest first; filter by city (the operator's scope), service and state (default: the lines
 *   waiting for the team, SUBMITTED + IN_REVIEW).
 * - A row opens the request's drawer (the vehicle named, what is reused, what is still to check, the equipment) with ONE primary move: start the review, or
 *   approve; "do not approve" asks a reason in an in-app confirmation. The list stays current in the background.
 * - A refused read is an error, never an empty list; no code reaches the screen (lines, states and reasons are words).
 */
export function ProviderServicesQueueScreen(): ReactElement {
  const { runtime, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, refusal, fail } = useVocab();
  const phoneOf = usePhoneText();
  const [market, setMarket] = useState('');
  const [line, setLine] = useState('');
  const [state, setState] = useState<QueueStateFilter>('WAITING');
  const [loaded, setLoaded] = useState<string | undefined>(undefined);
  const [view, setView] = useState<AdminServiceQueueView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<AdminServiceQueueRowView | undefined>(undefined);
  const seq = useRef(0);

  const load = useCallback(
    async (m: string, background = false): Promise<void> => {
      const n = ++seq.current;
      if (!background) {
        setError(undefined);
        setLoaded(m);
        setView(undefined);
      }
      if (m === '') return;
      const states = queueStates(state);
      const res = await runtime.providerServices.queue({ marketId: m, ...(line !== '' ? { line } : {}), ...(states !== undefined ? { states } : {}) });
      if (n !== seq.current) return;
      if (res.ok) {
        setError(undefined);
        setView(res.value);
      } else setError(t('psvc.queue.loadFailed', { reason: (refusal(SURFACE, res.error) ?? fail(res.error)).replace(/[.\s]+$/, '') }));
    },
    [runtime, line, state, t, refusal, fail],
  );

  useEffect(() => {
    void load(market);
  }, [market, load]);
  useAutoRefresh(() => load(market, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const tz = marketTimeZone(loaded, markets);
  const refresh = useCallback(() => void load(market, true), [load, market]);

  return (
    <>
      <PageHeader title={t('psvc.queue.title')} subtitle={t('psvc.queue.subtitle')} />

      <Panel><Stack gap={4}>
        <form onSubmit={(e) => e.preventDefault()}>
          <FormRow>
            <MarketField id="psvc-market" value={market} onChange={setMarket} />
            <Field label={t('psvc.queue.line')} htmlFor="psvc-line">
              <Select id="psvc-line" value={line} onChange={(e) => setLine(e.target.value)}>
                <option value="">{t('psvc.queue.line.all')}</option>
                {SERVICE_LINES.map((l) => <option key={l} value={l}>{v('provider_services.line', l)}</option>)}
              </Select>
            </Field>
            <Field label={t('psvc.queue.state')} htmlFor="psvc-state">
              <Select id="psvc-state" value={state} onChange={(e) => setState(e.target.value as QueueStateFilter)}>
                {QUEUE_STATE_FILTERS.map((s) => <option key={s} value={s}>{s === 'WAITING' ? t('psvc.queue.state.WAITING') : v('provider_services.enrollment_state', s)}</option>)}
              </Select>
            </Field>
          </FormRow>
        </form>
        {loaded === '' && <Notice tone="neutral">{t('psvc.queue.marketHint')}</Notice>}
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
      </Stack></Panel>

      <section aria-label={t('psvc.queue.list')}>
        <Panel padded={false}>
          <Table
            columns={[
              {
                key: 'provider',
                header: t('psvc.col.provider'),
                render: (r: AdminServiceQueueRowView) => (
                  <span>
                    {r.driverName ?? t('psvc.unnamed')}
                    {r.driverPhoneLast4 !== undefined && <Text role="caption" tone="tertiary" mono block>{phoneOf(`•••• ${r.driverPhoneLast4}`)}</Text>}
                  </span>
                ),
              },
              { key: 'line', header: t('psvc.col.line'), render: (r: AdminServiceQueueRowView) => v('provider_services.line', r.line) },
              { key: 'state', header: t('psvc.col.state'), render: (r: AdminServiceQueueRowView) => <Badge tone={enrollmentTone(r.state)}>{v('provider_services.enrollment_state', r.state)}</Badge> },
              { key: 'since', header: t('psvc.col.since'), render: (r: AdminServiceQueueRowView) => <span className="tnum">{formatMarketInstant(r.updatedAt, tz, lang)}</span> },
            ]}
            rows={view?.enrollments ?? []}
            rowKey={(r) => r.id}
            onRowClick={(r) => setOpen(r)}
            loading={loaded !== undefined && loaded !== '' && view === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('ui.loadFailed') : undefined}
            onRetry={() => void load(market)}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={loaded === undefined || loaded === '' ? t('psvc.queue.marketHint') : t('psvc.queue.empty')} />}
          />
        </Panel>
      </section>

      <ServiceRequestDrawer row={open} onClose={() => setOpen(undefined)} onChanged={refresh} />
    </>
  );
}
